import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { 
  ChevronLeft, Clock, MapPin, CheckCircle, Circle, 
  AlertTriangle, Phone, Navigation, Loader2, 
  ListChecks, FileText, Send, Mic, Plus,
  ChevronRight, X, MessageSquare
} from "lucide-react";
import CareNoteEntry from "../../components/employee/CareNoteEntry";

// Assistance types with icons and colors
const ASSISTANCE_TYPES = [
  { key: "running_late", label: "Running Late", icon: Clock, color: "amber" },
  { key: "client_refused_care", label: "Client Refused Care", icon: X, color: "red" },
  { key: "family_concern", label: "Family Concern", icon: MessageSquare, color: "orange" },
  { key: "medical_concern", label: "Medical Concern", icon: AlertTriangle, color: "red" },
  { key: "office_call_requested", label: "Office Call Requested", icon: Phone, color: "blue" },
  { key: "need_schedule_change", label: "Need Schedule Change", icon: Clock, color: "purple" },
  { key: "unable_to_enter_home", label: "Unable to Enter Home", icon: MapPin, color: "orange" },
  { key: "emergency", label: "Emergency", icon: AlertTriangle, color: "red" },
];

export default function ActiveVisit() {
  const { visitId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // State
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState(null);
  const [error, setError] = useState(null);
  const [currentStep, setCurrentStep] = useState("active"); // active, documenting, summary
  const [showAssistance, setShowAssistance] = useState(false);
  const [showAdditionalActivity, setShowAdditionalActivity] = useState(false);
  const [finishing, setFinishing] = useState(false);
  
  // Care notes state
  const [prompts, setPrompts] = useState([]);
  const [savedEntries, setSavedEntries] = useState({});
  const [savingEntry, setSavingEntry] = useState(false);

  const loadState = useCallback(async () => {
    try {
      const [stateRes, promptsRes] = await Promise.all([
        api.get(`/visit-workflow/${visitId}/state`),
        api.get("/care-notes/prompts"),
      ]);
      
      setState(stateRes.data);
      setPrompts(promptsRes.data);
      setCurrentStep(stateRes.data.workflow_step);
      
      // Load saved entries from care note
      if (stateRes.data.care_note?.guided_entries) {
        const entries = {};
        stateRes.data.care_note.guided_entries.forEach(e => {
          entries[e.prompt_key] = e;
        });
        setSavedEntries(entries);
      }
    } catch (err) {
      console.error("Failed to load visit state:", err);
      setError(err.response?.data?.detail || "Failed to load visit");
    } finally {
      setLoading(false);
    }
  }, [visitId]);

  useEffect(() => {
    loadState();
  }, [loadState]);

  const toggleActivity = async (activity) => {
    const isCompleted = state.completed_activity_ids?.includes(activity.activity_id);
    
    try {
      if (isCompleted) {
        await api.delete(`/visit-workflow/${visitId}/activity/${activity.activity_id}`);
      } else {
        await api.post(`/visit-workflow/${visitId}/activity`, {
          activity_id: activity.activity_id,
          activity_name: activity.activity_name,
          category: activity.category,
        });
      }
      await loadState();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to update activity");
    }
  };

  const requestAssistance = async (type) => {
    try {
      // Get current location
      let gps = null;
      try {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
        });
        gps = { 
          gps_latitude: position.coords.latitude, 
          gps_longitude: position.coords.longitude 
        };
      } catch (e) { /* GPS optional */ }

      await api.post(`/visit-workflow/${visitId}/assistance`, {
        assistance_type: type,
        ...gps,
      });
      
      setShowAssistance(false);
      await loadState();
      alert("Office has been notified.");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to request assistance");
    }
  };

  const startDocumentation = async () => {
    try {
      await api.post(`/visit-workflow/${visitId}/start-documentation`);
      setCurrentStep("documenting");
      await loadState();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to start documentation");
    }
  };

  const handleSaveEntry = async (entryData) => {
    setSavingEntry(true);
    try {
      const careNote = state.care_note;
      
      if (careNote?.id) {
        await api.put(`/care-notes/${careNote.id}/entry/${entryData.prompt_key}`, entryData);
      } else {
        await api.post(`/care-notes/visit/${visitId}`, {
          visit_id: visitId,
          entries: [entryData],
        });
      }
      
      // Update local state
      setSavedEntries(prev => ({
        ...prev,
        [entryData.prompt_key]: {
          ...entryData,
          final_response: entryData.response,
        },
      }));
      
      await loadState();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to save entry");
    } finally {
      setSavingEntry(false);
    }
  };

  const finishVisit = async () => {
    setFinishing(true);
    try {
      // Get GPS
      let gps = {};
      try {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 10000 });
        });
        gps = {
          gps_latitude: position.coords.latitude,
          gps_longitude: position.coords.longitude,
          gps_accuracy_meters: position.coords.accuracy,
        };
      } catch (e) { /* GPS optional */ }

      // Submit care note if exists
      if (state.care_note?.id && state.care_note.status === "draft") {
        try {
          await api.post(`/care-notes/${state.care_note.id}/submit`);
        } catch (e) {
          console.warn("Could not submit care note:", e);
        }
      }

      const result = await api.post(`/visit-workflow/${visitId}/finish`, {
        ...gps,
        device_info: navigator.userAgent,
      });
      
      setCurrentStep("summary");
      await loadState();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to finish visit");
    } finally {
      setFinishing(false);
    }
  };

  const openMaps = () => {
    if (state?.client?.address) {
      const encoded = encodeURIComponent(state.client.address);
      window.open(`https://www.google.com/maps/search/?api=1&query=${encoded}`, "_blank");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#06372B]" />
      </div>
    );
  }

  if (error && !state) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 mx-auto text-red-400 mb-3" />
          <div className="text-gray-900 font-medium">{error}</div>
          <button onClick={() => navigate(-1)} className="mt-4 text-[#06372B] font-medium">
            Go back
          </button>
        </div>
      </div>
    );
  }

  const { visit, client, authorized_activities, completed_activity_ids, timeline } = state || {};
  const clientName = client ? `${client.first_name} ${client.last_name}` : "Client";
  const activitiesComplete = completed_activity_ids?.length > 0;
  const docsComplete = state?.documentation_complete;

  // Progress steps
  const steps = [
    { key: "clock_in", label: "Clock In", complete: !!visit?.clock_in },
    { key: "activities", label: "Activities", complete: activitiesComplete },
    { key: "documentation", label: "Notes", complete: docsComplete },
    { key: "finish", label: "Finish", complete: !!visit?.clock_out },
  ];

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <header className="bg-[#06372B] text-white px-4 py-4 sticky top-0 z-40">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-full hover:bg-white/10">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-lg truncate">{clientName}</div>
              {client?.address && (
                <button 
                  onClick={openMaps}
                  className="text-sm text-white/70 flex items-center gap-1 hover:text-white"
                >
                  <MapPin className="w-3 h-3" />
                  <span className="truncate">{client.address}</span>
                </button>
              )}
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-4 flex items-center gap-1">
            {steps.map((step, i) => (
              <React.Fragment key={step.key}>
                <div className={`flex-1 h-1.5 rounded-full ${step.complete ? "bg-emerald-400" : "bg-white/20"}`} />
                {i < steps.length - 1 && <div className="w-1" />}
              </React.Fragment>
            ))}
          </div>
          <div className="mt-1 flex justify-between text-xs text-white/60">
            {steps.map(step => (
              <span key={step.key} className={step.complete ? "text-emerald-400" : ""}>
                {step.label}
              </span>
            ))}
          </div>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-5 space-y-5">
        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 shrink-0" />
            <div className="text-sm text-red-700 flex-1">{error}</div>
            <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Active Visit View */}
        {(currentStep === "active" || currentStep === "documenting") && !visit?.clock_out && (
          <>
            {/* Time Display */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-gray-500">Clocked in at</div>
                  <div className="text-2xl font-bold text-gray-900">
                    {visit?.clock_in?.timestamp 
                      ? new Date(visit.clock_in.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                      : "--:--"
                    }
                  </div>
                </div>
                <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                  <div className="w-3 h-3 rounded-full bg-emerald-500 animate-pulse" />
                </div>
              </div>
            </div>

            {/* Care Activities */}
            {currentStep === "active" && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ListChecks className="w-4 h-4 text-gray-400" />
                    <h2 className="font-semibold">Care Activities</h2>
                  </div>
                  <span className="text-sm text-gray-500">
                    {completed_activity_ids?.length || 0} / {authorized_activities?.length || 0}
                  </span>
                </div>
                
                <div className="divide-y divide-gray-100 max-h-64 overflow-y-auto">
                  {authorized_activities?.map((activity) => {
                    const isComplete = completed_activity_ids?.includes(activity.activity_id);
                    return (
                      <button
                        key={activity.activity_id}
                        onClick={() => toggleActivity(activity)}
                        className="w-full flex items-center gap-3 p-4 hover:bg-gray-50 transition-colors text-left"
                        data-testid={`activity-${activity.activity_id}`}
                      >
                        {isComplete ? (
                          <CheckCircle className="w-5 h-5 text-emerald-600 shrink-0" />
                        ) : (
                          <Circle className="w-5 h-5 text-gray-300 shrink-0" />
                        )}
                        <span className={isComplete ? "text-gray-900" : "text-gray-600"}>
                          {activity.activity_name}
                        </span>
                      </button>
                    );
                  })}
                </div>
                
                {/* Add Additional Activity */}
                <button
                  onClick={() => setShowAdditionalActivity(true)}
                  className="w-full flex items-center gap-3 p-4 border-t border-gray-100 text-[#06372B] hover:bg-gray-50 transition-colors"
                  data-testid="add-additional-activity"
                >
                  <Plus className="w-5 h-5" />
                  <span className="font-medium">Additional Care Activity Performed</span>
                </button>
              </div>
            )}

            {/* Documentation Section (when "Finish Visit" is clicked) */}
            {currentStep === "documenting" && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-gray-600">
                  <FileText className="w-4 h-4" />
                  <span className="font-medium">Documentation</span>
                </div>
                
                {prompts.map((prompt) => (
                  <CareNoteEntry
                    key={prompt.key}
                    prompt={prompt}
                    entry={savedEntries[prompt.key]}
                    onSave={handleSaveEntry}
                    isLoading={savingEntry}
                  />
                ))}
              </div>
            )}

            {/* Finish Visit / Continue Buttons */}
            <div className="space-y-3">
              {currentStep === "active" && (
                <button
                  onClick={startDocumentation}
                  className="w-full py-4 px-6 rounded-xl bg-[#06372B] text-white font-semibold flex items-center justify-center gap-2 hover:bg-[#0a4a3a] transition-colors"
                  data-testid="finish-visit-btn"
                >
                  <FileText className="w-5 h-5" />
                  Finish Visit
                </button>
              )}
              
              {currentStep === "documenting" && (
                <button
                  onClick={finishVisit}
                  disabled={finishing}
                  className="w-full py-4 px-6 rounded-xl bg-[#06372B] text-white font-semibold flex items-center justify-center gap-2 hover:bg-[#0a4a3a] transition-colors disabled:opacity-60"
                  data-testid="complete-visit-btn"
                >
                  {finishing ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                  Clock Out & Complete Visit
                </button>
              )}
            </div>
          </>
        )}

        {/* Completion Summary */}
        {(visit?.clock_out || currentStep === "summary") && (
          <div className="space-y-5">
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 text-center">
              <CheckCircle className="w-12 h-12 mx-auto text-emerald-600 mb-3" />
              <h2 className="text-xl font-bold text-emerald-900">Visit Complete</h2>
              <p className="text-emerald-700 mt-1">{clientName}</p>
            </div>
            
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 divide-y divide-gray-100">
              <div className="p-4 flex justify-between">
                <span className="text-gray-500">Hours Worked</span>
                <span className="font-semibold">{visit?.total_hours?.toFixed(2) || "0.00"} hrs</span>
              </div>
              <div className="p-4 flex justify-between">
                <span className="text-gray-500">Care Activities</span>
                <span className="font-semibold">{completed_activity_ids?.length || 0} completed</span>
              </div>
              <div className="p-4 flex justify-between">
                <span className="text-gray-500">Documentation</span>
                <span className={`font-semibold ${docsComplete ? "text-emerald-600" : "text-amber-600"}`}>
                  {docsComplete ? "Complete" : "Pending"}
                </span>
              </div>
            </div>

            {/* Timeline */}
            {timeline?.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100">
                  <h3 className="font-semibold">Visit Timeline</h3>
                </div>
                <div className="p-4 space-y-3">
                  {timeline.map((event, i) => (
                    <div key={i} className="flex gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#06372B] mt-2 shrink-0" />
                      <div className="flex-1">
                        <div className="text-sm text-gray-900">{event.description}</div>
                        <div className="text-xs text-gray-500">
                          {new Date(event.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!docsComplete && (
              <button
                onClick={() => navigate(`/employee/visit/${visitId}/care-notes`)}
                className="w-full py-4 px-6 rounded-xl bg-amber-50 text-amber-700 font-semibold flex items-center justify-center gap-2 border border-amber-200 hover:bg-amber-100 transition-colors"
              >
                <FileText className="w-5 h-5" />
                Complete Documentation
              </button>
            )}

            <button
              onClick={() => navigate("/employee/dashboard")}
              className="w-full py-3 px-6 rounded-xl border border-gray-200 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
            >
              Back to Dashboard
            </button>
          </div>
        )}
      </div>

      {/* Need Assistance FAB */}
      {!visit?.clock_out && (
        <button
          onClick={() => setShowAssistance(true)}
          className="fixed bottom-24 right-4 w-14 h-14 rounded-full bg-red-600 text-white shadow-lg flex items-center justify-center hover:bg-red-700 transition-colors z-30"
          data-testid="need-assistance-fab"
        >
          <AlertTriangle className="w-6 h-6" />
        </button>
      )}

      {/* Assistance Modal */}
      {showAssistance && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
          <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-md max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-red-600 text-white px-6 py-4 rounded-t-3xl sm:rounded-t-2xl flex items-center justify-between">
              <h2 className="font-semibold text-lg">Need Assistance</h2>
              <button onClick={() => setShowAssistance(false)} className="p-2 rounded-full hover:bg-white/20">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-2">
              {ASSISTANCE_TYPES.map((type) => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.key}
                    onClick={() => requestAssistance(type.key)}
                    className={`w-full flex items-center gap-3 p-4 rounded-xl border transition-colors ${
                      type.color === "red" 
                        ? "border-red-200 hover:bg-red-50"
                        : type.color === "amber"
                        ? "border-amber-200 hover:bg-amber-50"
                        : "border-gray-200 hover:bg-gray-50"
                    }`}
                    data-testid={`assistance-${type.key}`}
                  >
                    <Icon className={`w-5 h-5 ${
                      type.color === "red" ? "text-red-600" :
                      type.color === "amber" ? "text-amber-600" :
                      type.color === "orange" ? "text-orange-600" :
                      "text-gray-600"
                    }`} />
                    <span className="font-medium">{type.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Additional Activity Modal */}
      {showAdditionalActivity && (
        <AdditionalActivityModal
          visitId={visitId}
          onClose={() => setShowAdditionalActivity(false)}
          onSuccess={() => {
            setShowAdditionalActivity(false);
            loadState();
          }}
        />
      )}
    </div>
  );
}

// Additional Activity Modal Component
function AdditionalActivityModal({ visitId, onClose, onSuccess }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [reason, setReason] = useState("");
  const [requiresReview, setRequiresReview] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (!name.trim() || !description.trim() || !reason.trim()) {
      setError("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      await api.post(`/visit-workflow/${visitId}/additional-activity`, {
        activity_name: name,
        description,
        reason,
        requires_office_review: requiresReview,
      });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to add activity");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
      <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-md">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-semibold text-lg">Additional Care Activity</h2>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        
        <div className="p-6 space-y-4">
          {error && (
            <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Activity Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Grocery pickup"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#06372B] focus:ring-1 focus:ring-[#06372B] outline-none"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What did you do?"
              rows={3}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#06372B] focus:ring-1 focus:ring-[#06372B] outline-none resize-none"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why was this needed?"
              rows={2}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#06372B] focus:ring-1 focus:ring-[#06372B] outline-none resize-none"
            />
          </div>
          
          <label className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 cursor-pointer hover:bg-gray-50">
            <input
              type="checkbox"
              checked={requiresReview}
              onChange={(e) => setRequiresReview(e.target.checked)}
              className="w-5 h-5 rounded border-gray-300 text-[#06372B] focus:ring-[#06372B]"
            />
            <div>
              <div className="font-medium text-gray-900">Office Review Required</div>
              <div className="text-sm text-gray-500">Check if this needs HR/Admin approval</div>
            </div>
          </label>
        </div>
        
        <div className="px-6 py-4 border-t border-gray-100">
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full py-3 px-4 rounded-xl bg-[#06372B] text-white font-medium disabled:opacity-50 hover:bg-[#0a4a3a] transition-colors flex items-center justify-center gap-2"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Add Activity
          </button>
        </div>
      </div>
    </div>
  );
}
